import {PolymerElement} from '@polymer/polymer';
import {ScopeBinding, ScopeRoot} from '../behaviors/keli-binding-behavior';

class KeliChecklistController extends ScopeRoot(ScopeBinding(PolymerElement)) {
  static get is() {
    return 'keli-checklist-controller'
  }

  static get properties() {
    return {
      captionField: {type: String, scopevar: true},
      dataLoaded: {type: Boolean, value: false},
      element: {type: Object},
      linkingField: {type: String, scopevar: true},
      linkingFieldId: {type: String, scopevar: true},
      manualKeliReady: {type: Boolean, value: true},
      sortField: {type: String, scopevar: true},
      statusField: {type: String, scopevar: true}
    }
  }

  constructor() {
    super();
    this.addOnKeli({'uireloaddata': 'handleReloadData'});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.element = null;
  }

  async elementReady(element) {
    this.element = element;

    await this.changeKeliScopeRootName(this.element.keliname);
    await this.keliReady(this.element.getKeliScope());

    this.executeOnKeliReadyComplete(async () => {
      await this.runKeliFunction('uiSet', 'mode', 'browse');
      await this.loadChecklistItems();

      let openParams = await this.runKeliFunction('get', '_openparams');
      openParams = openParams || {};
      openParams.options = openParams.options || {};
      openParams.options.selectFields = openParams.options.selectFields || {};
      openParams.options.selectFields['id'] = true;
      if (this.statusField) {
        openParams.options.selectFields[this.statusField.toLowerCase()] = true;
      }
      if (this.captionField) {
        openParams.options.selectFields[this.captionField.toLowerCase()] = true;
      }
      if (this.sortField) {
        openParams.options.selectFields[this.sortField.toLowerCase()] = true;
      }
      await this.runKeliFunction('uiSet', '_openparams', openParams);
    });
  }

  async handleReloadData(event, templateInstance) {
    if (!this.dataLoaded) {
      return;
    }
    try {
      await this.loadChecklistItems(templateInstance);
    } catch (error) {
      if (!error.superceded) {
        console.error(error);
      }
    }
  }

  async loadChecklistItems(templateInstance) {
    if (!this.captionField) {
      keliUILogger(new Error('No caption field was specified.'));
      return;
    }

    templateInstance = this.getKeliTemplateInstance(templateInstance);
    let rowIndexNumber = 0;

    const checklistItems = [];
    while (await this.runKeliFunctionIn(templateInstance, 'rowsExists', rowIndexNumber)) {
      if (!this.isConnected) return;

      const item = {};
      item.id = await templateInstance.get(`rows.${rowIndexNumber}.id`);
      item.caption = await templateInstance.get(`rows.${rowIndexNumber}.${this.captionField}`);
      item.status = await templateInstance.get(`rows.${rowIndexNumber}.${this.statusField}`);
      item.order = await templateInstance.get(`rows.${rowIndexNumber}.${this.sortField}`);

      // Format the status.
      if (typeof item.status === 'string' && ['t', 'T', 'y', 'Y'].includes(item.status)) {
        item.status = true;
      } else if (typeof item.status !== 'boolean') {
        item.status = false;
      }
      item.textStatus = (item.status ? 'Y' : 'N');

      checklistItems.push(item);
      ++rowIndexNumber;
    }


    this.element.checklistItems = [];
    this.element.$.checklistItemsRepeat.render();
    this.element.checklistItems = checklistItems;

    if (!this.dataLoaded) {
      this.dataLoaded = true;
      this.dispatchEvent(new CustomEvent('dataLoaded', {
        bubbles: true,
        composed: true,
        detail: {element: this.element}
      }));
    }
  }

  async saveNewItem(caption) {
    if (!this.captionField || !this.linkingField || !this.linkingFieldId || !this.sortField) {
      keliUILogger(new Error('Missing information required to save checklist item.'));
      return;
    }

    let currentMaxOrder = Math.max(...this.element.checklistItems.map((item) => {
      return item.order || 0;
    }));

    const saveData = {};
    saveData['id'] = await this.runKeliFunction('uuid');
    saveData[this.captionField] = caption;
    saveData[this.linkingField] = this.linkingFieldId;
    saveData[this.sortField] = (parseInt(currentMaxOrder, 10) || 0) + 1;

    const templateObject = await this.runKeliFunction('get', '_template');
    await this.runKeliFunction('save', templateObject, saveData);
    return saveData.id;
  }

  async updateStatus(rowId, newStatus) {
    if (!rowId || !this.statusField) {
      keliUILogger(new Error('Missing information required to update checklist status.'));
      return;
    }

    let saveData = {};
    saveData['id'] = rowId;
    saveData[this.statusField] = newStatus;

    const templateObject = await this.runKeliFunction('get', '_template');
    await this.runKeliFunction('save', templateObject, saveData);
  }

  async updateChecklistOrder(items) {
    if (!this.sortField) {
      keliUILogger(new Error('Missing information required to update checklist order.'));
      return;
    }

    let saveData = [];
    for (let i = 0; i < items.length; ++i) {
      if (!items[i].id) {
        continue;
      }
      let row = {};
      row['id'] = items[i].id;
      row[this.sortField] = (i + 1);
      saveData.push(row);
    }

    const templateObject = await this.runKeliFunction('get', '_template');
    await this.runKeliFunction('save', templateObject, saveData);
  }
}

customElements.define(KeliChecklistController.is, KeliChecklistController);
export {KeliChecklistController};
